/* App env requirements editor: pure model. Row edits, key rules, the platform checkboxes and the save payload. */
import type { Platform } from '@hangar/shared';
import type { EnvRequirement } from '@/api/schemas';
import { PLATFORMS } from './envHelpers';
import { KEY_RE } from './envVarModel';

export const newRequirement = (): EnvRequirement => ({ key: '', required: true, pattern: null, environments: null, platforms: null, expectedVisibility: null, description: null, definedIn: 'ui' });

export const updateRow = (rows: EnvRequirement[], i: number, patch: Partial<EnvRequirement>): EnvRequirement[] => rows.map((row, j) => (j === i ? { ...row, ...patch } : row));
export const removeRow = (rows: EnvRequirement[], i: number): EnvRequirement[] => rows.filter((_, j) => j !== i);

/** Keys are typed as env var names: upper case, anything else becomes an underscore. */
export const normalizeRequirementKey = (raw: string): string => raw.toUpperCase().replace(/[^A-Z0-9_]/g, '_');

/** `config_file` rows come from hangar.yml: read-only here. */
export const isLocked = (r: EnvRequirement): boolean => r.definedIn === 'config_file';

const isDuplicate = (rows: EnvRequirement[], i: number): boolean => rows.findIndex((x) => x.key === rows[i]!.key) !== i;

/** Only editable (UI) rows are checked for a valid key; every row for duplicates. */
export const hasInvalidKey = (rows: EnvRequirement[]): boolean => rows.some((r) => r.definedIn === 'ui' && !KEY_RE.test(r.key));
export const hasDuplicateKey = (rows: EnvRequirement[]): boolean => rows.some((_, i) => isDuplicate(rows, i));

/** The inline error of row `i`, shown only after the first save attempt and never on locked rows. */
export function requirementKeyError(rows: EnvRequirement[], i: number, submitted: boolean): string | undefined {
  const r = rows[i]!;
  if (!submitted || isLocked(r)) return undefined;
  if (!KEY_RE.test(r.key)) return 'Uppercase, digits and underscores';
  return isDuplicate(rows, i) ? 'Duplicate key' : undefined;
}

/** Only `definedIn: ui` rows are written; config_file ones are kept by the API (409 if touched). */
export const rowsToSave = (rows: EnvRequirement[]): EnvRequirement[] => rows.filter((r) => r.definedIn !== 'config_file');

/** `null` platforms means "every platform". */
export const platformOn = (platforms: Platform[] | null | undefined, pl: Platform): boolean => !platforms || platforms.includes(pl);

/** Ticking every platform goes back to `null` (all). */
export function togglePlatform(platforms: Platform[] | null | undefined, pl: Platform, checked: boolean): Platform[] | null {
  const next = PLATFORMS.filter((p) => (p === pl ? checked : platformOn(platforms, p)));
  return next.length === PLATFORMS.length ? null : next;
}

export const savedDescription = (count: number, appSlug: string): string => `${count} rule${count === 1 ? '' : 's'} for ${appSlug}`;
